import React from 'react';
import { useNavigate } from 'react-router';
import { useTheme } from '../contexts/ThemeContext';
import { CaseSentSummary } from '../types/caseApi';
import { CustomTooltip } from './CustomTooltip';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faEye, faRotateLeft, faTimesCircle, faClock, faCheckCircle, faExclamationTriangle } from '@/icons';
import { CardTable } from './CardTable';

interface SentCasesTableProps {
    sentCases: CaseSentSummary[];
    loading: boolean;
    error: string | null;
    sortConfig: { key: string; direction: 'asc' | 'desc' } | null;
    onSort: (key: string) => void;
    rescindingCaseSentId: string | null;
    hoveredButton: { caseId: string; button: string } | null;
    onHoverButton: (caseId: string | null, button: string | null) => void;
    onRescindClick: (caseSentId: string) => void;
    getSortedCases: () => CaseSentSummary[];
    getSortIcon: (key: string) => React.ReactNode;
    styles: any; // Styles object from HomePage
}

export const SentCasesTable: React.FC<SentCasesTableProps> = ({
    sentCases,
    loading,
    error,
    sortConfig,
    onSort,
    rescindingCaseSentId,
    hoveredButton,
    onHoverButton,
    onRescindClick,
    getSortedCases,
    getSortIcon,
    styles,
}) => {
    const theme = useTheme();
    const navigate = useNavigate();

    const formatDate = (dateString: string) => {
        const date = new Date(dateString);
        const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = monthNames[date.getMonth()];
        const day = date.getDate();
        const year = date.getFullYear();
        return `${month} ${day}, ${year}`;
    };

    const formatStatus = (status: string | undefined) => {
        if (!status || status === 'Pending') return 'Pending';
        return status;
    };

    const renderSentCaseParties = (sentCase: CaseSentSummary) => {
        const partyA = sentCase.afn && sentCase.aln ? `${sentCase.afn} ${sentCase.aln}` : 'Party A';
        const partyB = sentCase.bfn && sentCase.bln ? `${sentCase.bfn} ${sentCase.bln}` : 'Party B';
        const isPetitionerB = sentCase.pet === 'B';

        if (isPetitionerB) {
            return (
                <div style={styles.partiesContainer}>
                    <div style={styles.partyA}>{partyB}</div>
                    <div style={styles.partyB}>{partyA}</div>
                </div>
            );
        } else {
            return (
                <div style={styles.partiesContainer}>
                    <div style={styles.partyA}>{partyA}</div>
                    <div style={styles.partyB}>{partyB}</div>
                </div>
            );
        }
    };

    const getStatusColor = (status?: string) => {
        switch (status) {
            case 'Accepted':
                return theme.colors.status?.success || '#10b981';
            case 'Pending':
                return theme.colors.status?.warning || '#f59e0b';
            case 'Rejected':
                return theme.colors.status?.error || '#dc2626';
            case 'Rescinded':
                return theme.colors.status?.error || '#dc2626';
            case 'Mixed':
                return theme.colors.primary || '#3b82f6';
            default:
                return theme.colors.status?.warning || '#f59e0b';
        }
    };

    const getStatusIcon = (status?: string) => {
        const iconStyle = { 
            color: getStatusColor(status), 
            marginRight: '4px',
            fontSize: theme.typography.fontSize.sm,
        };
        switch (status) {
            case 'Accepted':
                return <FontAwesomeIcon icon={faCheckCircle} style={iconStyle} />;
            case 'Pending':
                return <FontAwesomeIcon icon={faClock} style={iconStyle} />;
            case 'Rejected':
                return <FontAwesomeIcon icon={faTimesCircle} style={iconStyle} />;
            case 'Rescinded':
                return <FontAwesomeIcon icon={faRotateLeft} style={iconStyle} />;
            case 'Mixed':
                return <FontAwesomeIcon icon={faExclamationTriangle} style={iconStyle} />;
            default:
                return <FontAwesomeIcon icon={faClock} style={iconStyle} />;
        }
    };

    const renderSentTo = (sentCase: CaseSentSummary) => {
        if (sentCase.sentTo.length === 0) return 'N/A';

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                {sentCase.sentTo.map((recipient, idx) => (
                    <div key={idx} style={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: '2px',
                        marginBottom: idx < sentCase.sentTo.length - 1 ? theme.spacing.xs : 0
                    }}>
                        <div style={{ 
                            whiteSpace: 'nowrap' as const,
                            display: 'flex',
                            alignItems: 'center',
                            lineHeight: '1.5',
                        }}>
                            {recipient.userName}
                        </div>
                        {recipient.teamName && (
                            <div style={{
                                fontSize: theme.typography.fontSize.sm,
                                color: theme.colors.text.secondary,
                                wordWrap: 'break-word' as const,
                                whiteSpace: 'normal' as const,
                            }}>
                                {recipient.teamName}
                            </div>
                        )}
                    </div>
                ))}
            </div>
        );
    };

    const renderStatusColumn = (sentCase: CaseSentSummary) => {
        if (sentCase.sentTo.length === 0) return 'N/A';

        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                {/* Spacer to match the date line in Sent column */}
                <div style={{ whiteSpace: 'nowrap' as const, height: '1.5em' }}></div>
                {sentCase.sentTo.map((recipient, idx) => {
                    const recipientStatus = recipient.status || 'Pending';
                    const recipientStatusColor = getStatusColor(recipientStatus);
                    const canRescindThisRecipient = recipientStatus === 'Pending' && recipient.caseReceivedId;
                    const rescindButtonKey = `${sentCase.csid}-${recipient.caseReceivedId || idx}`;
                    const isRescinding = rescindingCaseSentId === rescindButtonKey;

                    return (
                        <div key={idx} style={{
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '2px',
                            marginBottom: idx < sentCase.sentTo.length - 1 ? theme.spacing.xs : 0
                        }}>
                            <div style={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: theme.spacing.xs,
                                lineHeight: '1.5',
                            }}>
                                <div style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '4px',
                                    padding: `${theme.spacing.xs} ${theme.spacing.sm}`,
                                    backgroundColor: recipientStatusColor + '15',
                                    border: `1px solid ${recipientStatusColor}`,
                                    borderRadius: theme.borderRadius.sm,
                                    fontSize: theme.typography.fontSize.sm,
                                    fontWeight: theme.typography.fontWeight.medium,
                                    color: recipientStatusColor,
                                    lineHeight: '1.5',
                                }}>
                                    {getStatusIcon(recipientStatus)}
                                    <span>{recipientStatus}</span>
                                </div>
                            </div>
                            {recipient.statusDate && (
                                <div style={{
                                    fontSize: theme.typography.fontSize.xs,
                                    color: theme.colors.text.secondary,
                                    marginLeft: '0px',
                                }}>
                                    {formatDate(recipient.statusDate)}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>
        );
    };


    if (loading) {
        return (
            <div style={styles.loadingMessage}>
                <img
                    src="/logo-spinner.gif"
                    alt="Loading..."
                    style={styles.loadingSpinner}
                />
                Loading sent cases...
            </div>
        );
    }

    if (error) {
        return <div style={styles.errorMessage}>{error}</div>;
    }

    if (sentCases.length === 0) {
        return (
            <div style={{
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                padding: theme.spacing.xl,
                textAlign: 'center',
                color: theme.colors.text.secondary,
            }}>
                <div style={{
                    fontSize: theme.typography.fontSize.xl,
                    marginBottom: theme.spacing.sm,
                }}>
                    <i className="fas fa-paper-plane" style={{ marginRight: theme.spacing.sm }}></i>
                    No sent cases found
                </div>
            </div>
        );
    }

    return (
        <div className="rw-scroll-x">
        <CardTable style={styles.table}>
            <thead>
                <tr style={styles.tableHeader}>
                    <th
                        style={{ ...styles.headerCell, ...styles.sortableHeader, textAlign: 'left' as const }}
                        onClick={() => onSort('canRescind')}
                    >
                        Actions {getSortIcon('canRescind')}
                    </th>
                    <th
                        style={{ ...styles.headerCell, ...styles.sortableHeader, textAlign: 'left' as const }}
                        onClick={() => onSort('parties')}
                    >
                        Parties {getSortIcon('parties')}
                    </th>
                    <th
                        style={{ ...styles.headerCell, ...styles.sortableHeader, textAlign: 'left' as const }}
                        onClick={() => onSort('sent')}
                    >
                        Sent {getSortIcon('sent')}
                    </th>
                    <th
                        style={{ ...styles.headerCell, ...styles.sortableHeader, textAlign: 'left' as const }}
                        onClick={() => onSort('status')}
                    >
                        Status {getSortIcon('status')}
                    </th>
                    <th
                        style={{ ...styles.headerCell, ...styles.sortableHeader, textAlign: 'left' as const }}
                        onClick={() => onSort('notes')}
                    >
                        Notes {getSortIcon('notes')}
                    </th>
                </tr>
            </thead>
            <tbody>
                {getSortedCases().map((sentCase, index) => {
                    const uniqueKey = `sent-case-${index}`;
                    const status = sentCase.status || 'Pending';
                    let bannerStyle: React.CSSProperties;
                    let statusIcon: any;
                    let bannerText: string;

                    if (status === 'Rescinded') {
                        bannerStyle = {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: theme.spacing.xs,
                            padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                            backgroundColor: (theme.colors.status?.error || '#dc2626') + '15',
                            border: `1px solid ${theme.colors.status?.error || '#dc2626'}`,
                            borderRadius: theme.borderRadius.sm,
                            fontSize: theme.typography.fontSize.sm,
                            fontWeight: theme.typography.fontWeight.medium,
                            color: theme.colors.status?.error || '#dc2626',
                            width: '100%',
                            minWidth: '120px',
                            marginBottom: theme.spacing.xs,
                        };
                        statusIcon = faTimesCircle;
                        bannerText = 'Rescinded';
                    } else if (status === 'Pending') {
                        bannerStyle = {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: theme.spacing.xs,
                            padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                            backgroundColor: (theme.colors.status?.warning || '#f59e0b') + '15',
                            border: `1px solid ${theme.colors.status?.warning || '#f59e0b'}`,
                            borderRadius: theme.borderRadius.sm,
                            fontSize: theme.typography.fontSize.sm,
                            fontWeight: theme.typography.fontWeight.medium,
                            color: theme.colors.status?.warning || '#f59e0b',
                            width: '100%',
                            minWidth: '120px',
                            marginBottom: theme.spacing.xs,
                        };
                        statusIcon = faClock;
                        bannerText = 'Pending';
                    } else if (status === 'Accepted') {
                        bannerStyle = {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: theme.spacing.xs,
                            padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                            backgroundColor: (theme.colors.status?.success || '#10b981') + '15',
                            border: `1px solid ${theme.colors.status?.success || '#10b981'}`,
                            borderRadius: theme.borderRadius.sm,
                            fontSize: theme.typography.fontSize.sm,
                            fontWeight: theme.typography.fontWeight.medium,
                            color: theme.colors.status?.success || '#10b981',
                            width: '100%',
                            minWidth: '120px',
                            marginBottom: theme.spacing.xs,
                        };
                        statusIcon = faCheckCircle;
                        bannerText = 'Accepted';
                    } else if (status === 'Mixed') {
                        bannerStyle = {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: theme.spacing.xs,
                            padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                            backgroundColor: (theme.colors.primary || '#3b82f6') + '15',
                            border: `1px solid ${theme.colors.primary || '#3b82f6'}`,
                            borderRadius: theme.borderRadius.sm,
                            fontSize: theme.typography.fontSize.sm,
                            fontWeight: theme.typography.fontWeight.medium,
                            color: theme.colors.primary || '#3b82f6',
                            width: '100%',
                            minWidth: '120px',
                            marginBottom: theme.spacing.xs,
                        };
                        statusIcon = faExclamationTriangle;
                        bannerText = 'Mixed';
                    } else {
                        // Default/unknown status
                        bannerStyle = {
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: theme.spacing.xs,
                            padding: `${theme.spacing.xs} ${theme.spacing.md}`,
                            backgroundColor: (theme.colors.status?.warning || '#f59e0b') + '15',
                            border: `1px solid ${theme.colors.status?.warning || '#f59e0b'}`,
                            borderRadius: theme.borderRadius.sm,
                            fontSize: theme.typography.fontSize.sm,
                            fontWeight: theme.typography.fontWeight.medium,
                            color: theme.colors.status?.warning || '#f59e0b',
                            width: '100%',
                            minWidth: '120px',
                            marginBottom: theme.spacing.xs,
                        };
                        statusIcon = faClock;
                        bannerText = formatStatus(status);
                    }

                    return (
                        <tr key={uniqueKey} style={{
                            ...styles.tableRow,
                            backgroundColor: index % 2 === 0 ? 'white' : '#f8f9fa',
                        }}>
                            <td style={styles.cell}>
                                <div style={{ display: 'flex', gap: theme.spacing.xs, alignItems: 'center' }}>
                                    <CustomTooltip
                                        text="View case as\noriginally sent"
                                        position="top"
                                        show={hoveredButton?.caseId === sentCase.csid && hoveredButton?.button === 'view'}
                                    >
                                        <button
                                            style={{
                                                ...styles.copyButton,
                                                cursor: !sentCase.cid ? 'not-allowed' : 'pointer',
                                                opacity: !sentCase.cid ? 0.6 : 1,
                                            }}
                                            onMouseEnter={() => onHoverButton(sentCase.csid, 'view')}
                                            onMouseLeave={() => onHoverButton(null, null)}
                                            onClick={() => {
                                                if (sentCase.cid) {
                                                    navigate(`/wizard/${sentCase.sc ?? 'IA'}/${sentCase.cid}/case`);
                                                }
                                            }}
                                            disabled={!sentCase.cid}
                                        >
                                            <FontAwesomeIcon icon={faEye} style={styles.buttonIcon} />
                                        </button>
                                    </CustomTooltip>
                                    {sentCase.canRescind && (
                                        <CustomTooltip
                                            text="Rescind sent file"
                                            position="top"
                                            show={hoveredButton?.caseId === sentCase.csid && hoveredButton?.button === 'rescind'}
                                        >
                                            <button
                                                style={{
                                                    ...styles.copyButton,
                                                    backgroundColor: theme.colors.status?.error || '#dc2626',
                                                    border: `1px solid ${theme.colors.status?.error || '#dc2626'}`,
                                                    cursor: rescindingCaseSentId === sentCase.csid ? 'not-allowed' : 'pointer',
                                                    opacity: rescindingCaseSentId === sentCase.csid ? 0.6 : 1,
                                                }}
                                                onMouseEnter={() => onHoverButton(sentCase.csid, 'rescind')}
                                                onMouseLeave={() => onHoverButton(null, null)}
                                                onClick={() => onRescindClick(sentCase.csid)}
                                                disabled={rescindingCaseSentId === sentCase.csid}
                                            >
                                                <FontAwesomeIcon icon={faRotateLeft} style={styles.buttonIcon} />
                                            </button>
                                        </CustomTooltip>
                                    )}
                                </div>
                            </td>
                            <td style={styles.cell}>
                                {(sentCase.afn || sentCase.bfn) ? renderSentCaseParties(sentCase) : 'N/A'}
                            </td>
                            <td style={styles.cell}>
                                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.xs }}>
                                    <div style={{ whiteSpace: 'nowrap' as const }}>{formatDate(sentCase.sendDate)}</div>
                                    <div style={{ maxWidth: '150px', wordWrap: 'break-word' as const }}>
                                        {renderSentTo(sentCase)}
                                    </div>
                                </div>
                            </td>
                            <td style={styles.cell}>
                                {renderStatusColumn(sentCase)}
                            </td>
                            <td style={{ ...styles.cell, verticalAlign: 'top' as const, padding: theme.spacing.xs }}>
                                {sentCase.messageNote ? (
                                    <div
                                        style={{
                                            width: '100%',
                                            height: '100%',
                                            padding: theme.spacing.xs,
                                            border: `1px solid ${theme.colors.border.default}`,
                                            borderRadius: theme.borderRadius.sm,
                                            fontSize: theme.typography.fontSize.md,
                                            fontFamily: theme.typography.fontFamily,
                                            backgroundColor: '#dbeafe',
                                            color: '#1e40af',
                                            overflowY: 'auto' as const,
                                            overflowX: 'hidden' as const,
                                            wordWrap: 'break-word' as const,
                                            whiteSpace: 'pre-wrap' as const,
                                            lineHeight: theme.typography.lineHeight.normal,
                                            boxSizing: 'border-box' as const,
                                        }}
                                    >
                                        {sentCase.messageNote}
                                    </div>
                                ) : (
                                    <div style={{ textAlign: 'center' as const }}>N/A</div>
                                )}
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        </CardTable>
        </div>
    );
};

